import { db } from '../db/indexeddb';
import { shouldMakeAPICall } from '../utils/network';
import axios from 'axios';
import { API_ENDPOINT } from '../../services/api/resources';


/**
 * Master Data Repository
 * Handles cost centers, GL codes, project codes, items, departments, vendors, users, PR codes
 */
export class MasterDataRepository {
  /**
   * Cost Centers
   */
  async getCostCentersFromCache() {
    try {
      const centers = await db.costCenters.toArray();
      return centers.map(c => c.data);
    } catch (error) {
      console.error('Error reading cost centers from cache:', error);
      return [];
    }
  }

  async saveCostCentersToCache(centers) {
    try {
      await db.transaction('rw', db.costCenters, async () => {
        await db.costCenters.clear();
        const data = centers.map(c => ({
          costcenter_code: c.costcenter_code || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.costCenters.bulkAdd(data);
      });
      console.log(`✅ Cached ${centers.length} cost centers`);
    } catch (error) {
      console.error('Error saving cost centers to cache:', error);
    }
  }

  async syncCostCenters(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getCostCentersFromCache();
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.cost_center_list,
        method: 'POST',
        data: { buyer_token: userToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data?.data || [];
      await this.saveCostCentersToCache(data);
      return data;
    } catch (error) {
      console.error('Error syncing cost centers:', error);
      return this.getCostCentersFromCache();
    }
  }

  /**
   * GL Codes
   */
  async getGLCodesFromCache() {
    try {
      const codes = await db.glCodes.toArray();
      return codes.map(c => c.data);
    } catch (error) {
      return [];
    }
  }

  async saveGLCodesToCache(codes) {
    try {
      await db.transaction('rw', db.glCodes, async () => {
        await db.glCodes.clear();
        const data = codes.map(c => ({
          gl_code: c.gl_code || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.glCodes.bulkAdd(data);
      });
      console.log(`✅ Cached ${codes.length} GL codes`);
    } catch (error) {
      console.error('Error saving GL codes to cache:', error);
    }
  }

  async syncGLCodes(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getGLCodesFromCache();
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.gl_code_list,
        method: 'POST',
        data: { buyer_token: userToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const payload = response?.data;
      const data = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.results)
          ? payload.results
          : [];
      await this.saveGLCodesToCache(data);
      return data;
    } catch (error) {
      return this.getGLCodesFromCache();
    }
  }

  /**
   * Project Codes
   */
  async getProjectCodesFromCache() {
    try {
      const codes = await db.projectCodes.toArray();
      return codes.map(c => c.data);
    } catch (error) {
      return [];
    }
  }

  async saveProjectCodesToCache(codes) {
    try {
      await db.transaction('rw', db.projectCodes, async () => {
        await db.projectCodes.clear();
        const data = codes.map(c => ({
          project_code: c.project_code || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.projectCodes.bulkAdd(data);
      });
      console.log(`✅ Cached ${codes.length} project codes`);
    } catch (error) {
      console.error('Error saving project codes to cache:', error);
    }
  }

  async syncProjectCodes(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getProjectCodesFromCache();
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.projects_list,
        method: 'POST',
        data: { buyer_token: userToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const payload = response?.data;
      const data = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.results)
          ? payload.results
          : [];
      await this.saveProjectCodesToCache(data);
      return data;
    } catch (error) {
      return this.getProjectCodesFromCache();
    }
  }

  /**
   * Items
   */
  async getItemsFromCache() {
    try {
      const items = await db.items.toArray();
      return items.map(i => i.data);
    } catch (error) {
      return [];
    }
  }

  async saveItemsToCache(items) {
    try {
      await db.transaction('rw', db.items, async () => {
        await db.items.clear();
        const data = items.map(item => ({
          item_code: item.item_code || '',
          item_token: item.item_token || '',
          data: item,
          lastSynced: Date.now(),
        }));
        await db.items.bulkAdd(data);
      });
      console.log(`✅ Cached ${items.length} items`);
    } catch (error) {
      console.error('Error saving items to cache:', error);
    }
  }

  async syncItems(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getItemsFromCache();
    }

    try {
      const response = await axios.post(
        API_ENDPOINT.view_all_item,
        { org_token: userToken },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const data = response?.data?.response || [];
      await this.saveItemsToCache(data);
      return data;
    } catch (error) {
      return this.getItemsFromCache();
    }
  }

  /** Bind items from IndexedDB (db.items). Use when offline. */
  async getItemsFromIndexedDB() {
    return this.getItemsFromCache();
  }

  /**
   * Departments
   */
  async getDepartmentsFromCache() {
    try {
      const depts = await db.departments.toArray();
      return depts.map(d => d.data);
    } catch (error) {
      return [];
    }
  }

  async saveDepartmentsToCache(departments) {
    try {
      await db.transaction('rw', db.departments, async () => {
        await db.departments.clear();
        const data = departments.map(d => ({
          name: d.name || '',
          data: d,
          lastSynced: Date.now(),
        }));
        await db.departments.bulkAdd(data);
      });
      console.log(`✅ Cached ${departments.length} departments`);
    } catch (error) {
      console.error('Error saving departments to cache:', error);
    }
  }

  async syncDepartments(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getDepartmentsFromCache();
    }

    try {
      const response = await axios.post(
        API_ENDPOINT.get_Department,
        { user_token: userToken },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const data = response?.data?.data || [];
      await this.saveDepartmentsToCache(data);
      return data;
    } catch (error) {
      return this.getDepartmentsFromCache();
    }
  }

  /**
   * Vendors
   */
  async getVendorsFromCache() {
    try {
      const vendors = await db.vendors.toArray();
      return vendors.map(v => v.data);
    } catch (error) {
      return [];
    }
  }

  async saveVendorsToCache(vendors) {
    try {
      await db.transaction('rw', db.vendors, async () => {
        await db.vendors.clear();
        const data = vendors.map(v => ({
          token: v.token || '',
          data: v,
          lastSynced: Date.now(),
        }));
        await db.vendors.bulkAdd(data);
      });
      console.log(`✅ Cached ${vendors.length} vendors`);
    } catch (error) {
      console.error('Error saving vendors to cache:', error);
    }
  }

  async syncVendors(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getVendorsFromCache();
    }

    try {
      const response = await axios({
        method: 'POST',
        url: API_ENDPOINT.get_all_vendor,
        data: { org_token: userToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data?.users || [];
      await this.saveVendorsToCache(data);
      return data;
    } catch (error) {
      return this.getVendorsFromCache();
    }
  }

  /**
   * Users
   */
  async getUsersFromCache() {
    try {
      const users = await db.users.toArray();
      return users.map(u => u.data);
    } catch (error) {
      return [];
    }
  }

  /** Bind users from IndexedDB (db.users). Use when offline. */
  async getUsersFromIndexedDB() {
    return this.getUsersFromCache();
  }

  async saveUsersToCache(users) {
    try {
      await db.transaction('rw', db.users, async () => {
        await db.users.clear();
        const data = users.map(u => ({
          token: u.token || '',
          data: u,
          lastSynced: Date.now(),
        }));
        await db.users.bulkAdd(data);
      });
      console.log(`✅ Cached ${users.length} users`);
    } catch (error) {
      console.error('Error saving users to cache:', error);
    }
  }

  async syncUsers(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getUsersFromCache();
    }

    try {
      const response = await axios.post(
        API_ENDPOINT.all_user_list,
        { buyer_token: userToken },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const data = response?.data || [];
      await this.saveUsersToCache(data);
      return data;
    } catch (error) {
      return this.getUsersFromCache();
    }
  }

  /**
   * PR Codes
   */
  async getPRCodesFromCache() {
    try {
      const codes = await db.prCodes.toArray();
      return codes.map(c => c.data);
    } catch (error) {
      return [];
    }
  }

  async savePRCodesToCache(codes) {
    try {
      await db.transaction('rw', db.prCodes, async () => {
        await db.prCodes.clear();
        const data = codes.map(c => ({
          pr_code: c.pr_code || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.prCodes.bulkAdd(data);
      });
      console.log(`✅ Cached ${codes.length} PR codes`);
    } catch (error) {
      console.error('Error saving PR codes to cache:', error);
    }
  }

  async syncPRCodes(authToken) {
    if (!shouldMakeAPICall(authToken, '')) {
      return this.getPRCodesFromCache();
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.get_all_saved_prs_code,
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data || [];
      await this.savePRCodesToCache(data);
      return data;
    } catch (error) {
      return this.getPRCodesFromCache();
    }
  }

  /**
   * Countries - for RFQ shipping address
   */
  async getCountriesFromCache() {
    try {
      const countries = await db.countries.toArray();
      return countries.map(c => c.data);
    } catch (error) {
      console.error('Error reading countries from cache:', error);
      return [];
    }
  }

  async saveCountriesToCache(countries) {
    try {
      await db.transaction('rw', db.countries, async () => {
        await db.countries.clear();
        const data = countries.map(c => ({
          token: c.token || c.id || '',
          name: c.name || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.countries.bulkAdd(data);
      });
      console.log(`✅ Cached ${countries.length} countries`);
    } catch (error) {
      console.error('Error saving countries to cache:', error);
    }
  }

  async syncCountries(authToken) {
    if (!shouldMakeAPICall(authToken, '')) {
      return this.getCountriesFromCache();
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.view_countries,
        method: 'GET',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data?.response || response?.data || [];
      await this.saveCountriesToCache(data);
      return data;
    } catch (error) {
      console.error('Error syncing countries:', error);
      return this.getCountriesFromCache();
    }
  }

  /**
   * States - for RFQ shipping address
   */
  async getStatesFromCache(countryTokenOrId) {
    try {
      // If countryTokenOrId is a number, it's country_id; if string, it's country_token
      const isNumber = typeof countryTokenOrId === 'number';
      let states;
      
      if (countryTokenOrId) {
        if (isNumber) {
          // Query by country_id (number)
          states = await db.states.where('country_id').equals(countryTokenOrId).toArray();
          console.log(`🔍 Querying states by country_id=${countryTokenOrId}, found ${states.length} in index`);
          
          // If index query returns empty, fall back to getting all and filtering
          if (states.length === 0) {
            console.log(`⚠️ Index query returned empty, falling back to client-side filter`);
            const allStates = await db.states.toArray();
            states = allStates.filter(s => {
              const stateData = s.data || s;
              // Check both the data object and the cache object's country_id
              // Also handle number/string type mismatches
              const dataCountryId = stateData?.country_id;
              const cacheCountryId = s.country_id;
              const targetId = countryTokenOrId;
              return Number(dataCountryId) === Number(targetId) || 
                     Number(cacheCountryId) === Number(targetId) ||
                     dataCountryId === targetId || 
                     cacheCountryId === targetId;
            });
            console.log(`📦 Client-side filter found ${states.length} states`);
          }
        } else {
          // Query by country_token (string)
          states = await db.states.where('country_token').equals(countryTokenOrId).toArray();
        }
      } else {
        // Get all states
        states = await db.states.toArray();
      }
      
      // Return the actual state objects (unwrap from cache structure)
      return states.map(s => s.data || s);
    } catch (error) {
      console.error('Error reading states from cache:', error);
      return [];
    }
  }

  async saveStatesToCache(states, countryToken) {
    try {
      await db.transaction('rw', db.states, async () => {
        // Clear states for this country if countryToken provided
        if (countryToken) {
          await db.states.where('country_token').equals(countryToken).delete();
        } else {
          await db.states.clear();
        }
        
        const data = states.map(s => ({
          token: s.token || s.id || '',
          country_token: countryToken || s.country_token || s.country_id || '',
          country_id: s.country_id || s.data?.country_id || '', // Store country_id (number) for filtering
          name: s.name || s.state_name || '',
          data: s,
          lastSynced: Date.now(),
        }));
        await db.states.bulkAdd(data);
      });
      console.log(`✅ Cached ${states.length} states${countryToken ? ` for country ${countryToken}` : ''}`);
    } catch (error) {
      console.error('Error saving states to cache:', error);
    }
  }

  async syncStates(authToken, countryToken) {
    if (!shouldMakeAPICall(authToken, '')) {
      return this.getStatesFromCache(countryToken);
    }

    try {
      // Use all_view_states API to get all states
      const response = await axios({
        url: API_ENDPOINT.all_view_states,
        method: 'GET',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const allStates = response?.data?.response || response?.data || [];
      
      // Filter by country if countryToken provided
      const filteredStates = countryToken 
        ? allStates.filter(s => s.country_token === countryToken || s.country_id === countryToken)
        : allStates;
      
      // Save all states to cache (for future use)
      await this.saveStatesToCache(allStates, null);
      
      return filteredStates;
    } catch (error) {
      console.error('Error syncing states:', error);
      return this.getStatesFromCache(countryToken);
    }
  }

  /**
   * Cities - for RFQ shipping address
   */
  async getCitiesFromCache(stateTokenOrId) {
    try {
      // If stateTokenOrId is a number, it's state_id; if string, it's state_token
      const isNumber = typeof stateTokenOrId === 'number';
      let cities;
      
      if (stateTokenOrId) {
        if (isNumber) {
          // Query by state_id (number)
          cities = await db.cities.where('state_id').equals(stateTokenOrId).toArray();
          console.log(`🔍 Querying cities by state_id=${stateTokenOrId}, found ${cities.length} in index`);
          
          // If index query returns empty, fall back to getting all and filtering
          if (cities.length === 0) {
            console.log(`⚠️ Index query returned empty, falling back to client-side filter`);
            const allCities = await db.cities.toArray();
            cities = allCities.filter(c => {
              const cityData = c.data || c;
              // Check both the data object and the cache object's state_id
              // Also handle number/string type mismatches
              const dataStateId = cityData?.state_id;
              const cacheStateId = c.state_id;
              const targetId = stateTokenOrId;
              return Number(dataStateId) === Number(targetId) || 
                     Number(cacheStateId) === Number(targetId) ||
                     dataStateId === targetId || 
                     cacheStateId === targetId;
            });
            console.log(`📦 Client-side filter found ${cities.length} cities`);
          }
        } else {
          // Query by state_token (string)
          cities = await db.cities.where('state_token').equals(stateTokenOrId).toArray();
        }
      } else {
        // Get all cities
        cities = await db.cities.toArray();
      }
      
      // Return the actual city objects (unwrap from cache structure)
      // If c.data exists, return it (the original API object with state_id)
      // Otherwise return c itself (if it's already unwrapped)
      return cities.map(c => {
        const cityObj = c.data || c;
        // Ensure state_id is preserved from the original data
        if (c.data && c.state_id && !cityObj.state_id) {
          cityObj.state_id = c.state_id;
        }
        return cityObj;
      });
    } catch (error) {
      console.error('Error reading cities from cache:', error);
      return [];
    }
  }

  async saveCitiesToCache(cities, stateToken) {
    try {
      await db.transaction('rw', db.cities, async () => {
        // Clear cities for this state if stateToken provided
        if (stateToken) {
          await db.cities.where('state_token').equals(stateToken).delete();
        } else {
          await db.cities.clear();
        }
        
        const data = cities.map(c => ({
          token: c.token || c.id || '',
          state_token: stateToken || c.state_token || c.state_id || '',
          state_id: c.state_id || c.data?.state_id || '', // Store state_id (number) for filtering
          name: c.name || c.city_name || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.cities.bulkAdd(data);
      });
      console.log(`✅ Cached ${cities.length} cities${stateToken ? ` for state ${stateToken}` : ''}`);
    } catch (error) {
      console.error('Error saving cities to cache:', error);
    }
  }

  async syncCities(authToken, stateToken) {
    if (!shouldMakeAPICall(authToken, '')) {
      return this.getCitiesFromCache(stateToken);
    }

    try {
      // Use all_view_cities API to get all cities
      const response = await axios({
        url: API_ENDPOINT.all_view_cities,
        method: 'GET',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const allCities = response?.data?.response || response?.data || [];
      
      // Filter by state if stateToken provided
      const filteredCities = stateToken 
        ? allCities.filter(c => c.state_token === stateToken || c.state_id === stateToken)
        : allCities;
      
      // Save all cities to cache (for future use)
      await this.saveCitiesToCache(allCities, null);
      
      return filteredCities;
    } catch (error) {
      console.error('Error syncing cities:', error);
      return this.getCitiesFromCache(stateToken);
    }
  }

  /**
   * RFQ Categories
   */
  async getRFQCategoriesFromCache() {
    try {
      const categories = await db.rfqCategories.toArray();
      return categories.map(c => c.data);
    } catch (error) {
      console.error('Error reading RFQ categories from cache:', error);
      return [];
    }
  }

  async saveRFQCategoriesToCache(categories) {
    try {
      await db.transaction('rw', db.rfqCategories, async () => {
        await db.rfqCategories.clear();
        const data = categories.map(c => ({
          token: c.token || c.id || '',
          name: c.name || c.category_name || '',
          data: c,
          lastSynced: Date.now(),
        }));
        await db.rfqCategories.bulkAdd(data);
      });
      console.log(`✅ Cached ${categories.length} RFQ categories`);
    } catch (error) {
      console.error('Error saving RFQ categories to cache:', error);
    }
  }

  /**
   * RFQ Categories: gate by online + authenticated (userToken used for consistency).
   */
  async syncRFQCategories(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getRFQCategoriesFromCache();
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.list_of_prompt_category,
        method: 'GET',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data?.response || response?.data || [];
      await this.saveRFQCategoriesToCache(data);
      return data;
    } catch (error) {
      console.error('Error syncing RFQ categories:', error);
      return this.getRFQCategoriesFromCache();
    }
  }

  /**
   * Shipping Addresses - for RFQ
   */
  async getShippingAddressesFromCache(buyerToken) {
    try {
      const addresses = buyerToken
        ? await db.shippingAddresses.where('buyer_token').equals(buyerToken).toArray()
        : await db.shippingAddresses.toArray();
      return addresses.map(a => a.data);
    } catch (error) {
      console.error('Error reading shipping addresses from cache:', error);
      return [];
    }
  }

   /**
    * @description Sync purchase orders from API
    */

   async syncPurchaseOrder(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getPurchaseOrdersFromCache(userToken);
    }
    try {
      const response = await axios({
        url: API_ENDPOINT.get_purchase_order,
        method: 'GET',
        params: { user_token: userToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data?.response || response?.data || [];
      await this.savePurchaseOrdersToCache(data);
      return data;
    } catch (error) {
      console.error('Error syncing purchase orders:', error);
      return this.getPurchaseOrdersFromCache(userToken);
    }
   
  }
   

  async saveShippingAddressesToCache(addresses, buyerToken) {
    try {
      await db.transaction('rw', db.shippingAddresses, async () => {
        if (buyerToken) {
          await db.shippingAddresses.where('buyer_token').equals(buyerToken).delete();
        } else {
          await db.shippingAddresses.clear();
        }
        
        const data = addresses.map(a => ({
          buyer_token: buyerToken || a.buyer_token || '',
          data: a,
          lastSynced: Date.now(),
        }));
        await db.shippingAddresses.bulkAdd(data);
      });
      console.log(`✅ Cached ${addresses.length} shipping addresses`);
    } catch (error) {
      console.error('Error saving shipping addresses to cache:', error);
    }
  }

  async syncShippingAddresses(authToken, buyerToken) {
    if (!shouldMakeAPICall(authToken, buyerToken)) {
      console.log('📴 Offline - using cached shipping addresses');
      return this.getShippingAddressesFromCache(buyerToken);
    }

    try {
      const response = await axios({
        url: API_ENDPOINT.buyer_shipping_address,
        method: 'POST',
        data: { buyer_token: buyerToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response?.data?.response || response?.data || [];
      await this.saveShippingAddressesToCache(data, buyerToken);
      return data;
    } catch (error) {
      // Network errors are expected when offline - silently use cache
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED') {
        console.log('📴 Network error - using cached shipping addresses');
      } else {
        console.error('Error syncing shipping addresses:', error);
      }
      return this.getShippingAddressesFromCache(buyerToken);
    }
  }

  /** Bind shipping addresses from IndexedDB (db.shippingAddresses). Use when offline. */
  async getShippingAddressesFromIndexedDB(buyerToken) {
    return this.getShippingAddressesFromCache(buyerToken);
  }

  

  

}

export const masterDataRepository = new MasterDataRepository();
